/**
 * Akustisches Feedback im Browser (Wunsch Niggo 2026-09-13): Tippen, richtige/
 * falsche Antwort, Runde gewonnen/verloren, dazu Lautsprecher-Symbol mit
 * Regler (Standard 50%). Bewusst synthetisch per Web Audio API statt
 * Audiodateien - keine Assets, kein Lizenzthema, alles im Code einstellbar.
 *
 * Nur im Web (Konzept-Erweiterung "sounds im Web") - der SSH-Client bekommt
 * das bewusst nicht, siehe `Terminal.sound?()` als optionale Methode.
 */

export type SoundCue = 'type' | 'backspace' | 'correct' | 'wrong' | 'win' | 'lose';

interface Note {
  freq: number;
  start: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
}

const SPECS: Record<SoundCue, Note[]> = {
  type: [{ freq: 1400, start: 0, dur: 0.02, type: 'square', gain: 0.25 }],
  backspace: [{ freq: 650, start: 0, dur: 0.035, type: 'square', gain: 0.3 }],
  correct: [
    { freq: 523.25, start: 0, dur: 0.1 },
    { freq: 783.99, start: 0.08, dur: 0.2 },
  ],
  wrong: [
    { freq: 220, start: 0, dur: 0.18, type: 'sawtooth', gain: 0.5 },
    { freq: 185, start: 0.1, dur: 0.24, type: 'sawtooth', gain: 0.5 },
  ],
  win: [
    { freq: 523.25, start: 0, dur: 0.12 },
    { freq: 659.25, start: 0.1, dur: 0.12 },
    { freq: 783.99, start: 0.2, dur: 0.12 },
    { freq: 1046.5, start: 0.3, dur: 0.4 },
  ],
  lose: [
    { freq: 392, start: 0, dur: 0.2, type: 'triangle' },
    { freq: 349.23, start: 0.18, dur: 0.2, type: 'triangle' },
    { freq: 293.66, start: 0.36, dur: 0.45, type: 'triangle' },
  ],
};

const STORAGE_KEY = 'dq.volume.v1';

class SoundManager {
  private ctx: AudioContext | null = null;
  private volume = 0.5;
  private muted = false;

  constructor() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw !== null) this.volume = Math.min(1, Math.max(0, Number(raw)));
    } catch {
      /* privater Modus o. ä. - Standardlautstärke 50% bleibt */
    }
  }

  get percent(): number {
    return Math.round(this.volume * 100);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setVolume(percent: number): void {
    this.volume = Math.min(1, Math.max(0, percent / 100));
    this.muted = false;
    this.persist();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  play(cue: SoundCue): void {
    if (this.muted || this.volume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    for (const note of SPECS[cue]) this.playNote(ctx, note);
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.volume));
    } catch {
      /* ignorieren */
    }
  }

  /** AudioContext darf laut Browser-Regel erst nach einer Nutzeraktion starten - play() wird nur durch Tastendruck ausgelöst. */
  private ensureContext(): AudioContext | null {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  private playNote(ctx: AudioContext, note: Note): void {
    const now = ctx.currentTime + note.start;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = note.type ?? 'sine';
    osc.frequency.setValueAtTime(note.freq, now);

    const peak = this.volume * 0.25 * (note.gain ?? 1);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + note.dur);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + note.dur + 0.02);
  }
}

export const sound = new SoundManager();
